/* eslint-disable react/jsx-no-useless-fragment */
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import type { ChangeEvent, CSSProperties, FC, PropsWithChildren } from 'react';
import ReactSlider from 'react-slider';

import ErrorAccordion from '@/components/ErrorAccordion';

import Label from './Label';
import TextInputField from './TextInputField';

export type ThumbStyleProps = VariantProps<ReturnType<typeof thumbStyle>>;
const trackStyle = ({
  index,
  disabled,
  error,
  isTrackColorInverted,
}: {
  index?: number;
  disabled?: boolean;
  error?: string;
  isTrackColorInverted?: boolean;
}) => {
  const isInRange = isTrackColorInverted ? index === 0 : index === 1;
  return cva('', {
    variants: {
      variant: {
        transparent: classNames(
          `h-1 cursor-pointer rounded-sm bg-neutral-700`,
          {
            'bg-primary-500': isInRange && !disabled,
            'bg-secondary-700/20': !isInRange && !disabled,
            '!bg-error-84%': isInRange && !disabled && error,
            '!bg-error-20%': !isInRange && !disabled && error,
          }
        ),
      },
    },
    defaultVariants: {
      variant: 'transparent',
    },
  });
};

export type TrackStyleProps = VariantProps<ReturnType<typeof trackStyle>>;
const thumbStyle = ({
  disabled,
  error,
}: {
  disabled?: boolean;
  error?: string;
}) =>
  cva('', {
    variants: {
      variant: {
        transparent: classNames(
          'bg-neutral-500 h-6 w-6 rounded-full cursor-pointer outline-none shadow-custom border-primary-200 border-[6px] !z-0 shadow-neutral-700 transition-shadow',
          {
            'shadow-primary-500 bg-primary-500 focus:shadow-primary-600 focus:bg-primary-600':
              !disabled,
            '!shadow-error-84% !bg-error': error && !disabled,
          }
        ),
      },
    },
    defaultVariants: {
      variant: 'transparent',
    },
  });

export interface SelectVariantProps extends TrackStyleProps, ThumbStyleProps {}

type Props<T> = {
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  defaultValue?: (T | undefined extends T ? never : T) | undefined;
  value?: T;
  onChange?: (value: T) => void;
  showInputField?: boolean;
  className?: string;
  label?: string;
  error?: string;
  name?: string;
  placeholder?: T extends number[] ? string[] : string;
  showInputFieldsOnSides?: boolean;
  style?: CSSProperties;
  isTrackColorInverted?: boolean;
} & SelectVariantProps;

const SliderInput = <T extends number | number[]>({
  value,
  onChange,
  disabled,
  showInputField = false,
  className,
  label,
  error,
  placeholder,
  step,
  min,
  max,
  showInputFieldsOnSides,
  style,
  variant,
  isTrackColorInverted,
  ...props
}: Props<T>) => {
  const getValue = (currentValue?: T): T => {
    if (Array.isArray(currentValue)) {
      return [
        Number.isNaN(currentValue[0]) ? min : currentValue[0],
        Number.isNaN(currentValue[1]) ? max : currentValue[1],
      ] as T;
    }
    return (Number.isNaN(currentValue) ? 0 : currentValue) as T;
  };

  return (
    <div style={style} className={classNames('', className)}>
      {!!label && (
        <div className="mb-2">
          <Label>{label}</Label>
        </div>
      )}
      <div
        className={classNames('', {
          'grid grid-cols-[auto,1fr,auto] gap-3':
            Array.isArray(value) && showInputField && showInputFieldsOnSides,
          'flex w-full flex-col gap-4':
            Array.isArray(value) && showInputField && !showInputFieldsOnSides,
          'flex w-full flex-row items-center justify-center gap-3':
            !Array.isArray(value) && showInputField,
        })}
      >
        {Array.isArray(value) && showInputField && (
          <TextInputWrap shouldWrap={!showInputFieldsOnSides}>
            {[0, 1].map((indexToModify) => (
              <TextInputFieldArray
                variant={variant}
                fieldClassName={
                  indexToModify === 1 && showInputFieldsOnSides
                    ? 'order-1'
                    : undefined
                }
                key={indexToModify}
                step={step}
                min={min}
                max={max}
                disabled={disabled}
                indexToModify={indexToModify}
                placeholder={
                  Array.isArray(placeholder) ? placeholder : undefined
                }
                error={error}
                canHaveMessage={false}
                className="w-full"
                inputClassName="!py-2"
                value={value}
                onChange={(newValue) => onChange?.(newValue as T)}
              />
            ))}
          </TextInputWrap>
        )}
        <ReactSlider
          {...props}
          min={min}
          max={max}
          step={step}
          onChange={onChange}
          disabled={disabled}
          className="flex w-full items-center justify-center p-4"
          thumbClassName={thumbStyle({ disabled, error })({ variant })}
          value={getValue(value)}
          renderTrack={(prop, state) => (
            <div
              key={state.index}
              style={prop.style}
              className={trackStyle({
                disabled,
                error,
                index: state.index,
                isTrackColorInverted,
              })({
                variant,
              })}
            />
          )}
        />
        {!Array.isArray(value) && showInputField && (
          <TextInputField
            type="number"
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            placeholder={!Array.isArray(placeholder) ? placeholder : ''}
            error={error}
            canHaveErrorMessage={false}
            variant={variant}
            className="!px-2"
            inputClassName="!py-2 text-center"
            value={Number.isNaN(value) ? '' : value}
            onChange={(newValue) => {
              onChange?.(parseFloat(newValue.target.value) as T);
            }}
            onBlur={() => {
              if (Number.isNaN(value)) {
                onChange?.(0 as T);
              }
            }}
          />
        )}
      </div>
      {error && <ErrorAccordion error={error} />}
    </div>
  );
};

type TextInputFieldArrayProps = {
  indexToModify: number;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  placeholder?: string[];
  error?: string;
  canHaveMessage?: boolean;
  className?: string;
  inputClassName?: string;
  value: number[];
  onChange: (value: number[]) => void;
  fieldClassName?: string;
} & SelectVariantProps;

const TextInputFieldArray = ({
  indexToModify,
  value,
  onChange,
  variant = 'transparent',
  ...props
}: TextInputFieldArrayProps) => {
  const firstValue = value[0];
  const secondValue = value[1];
  const isFirstValueComponent = indexToModify === 0;
  const isSecondValueComponent = indexToModify === 1;

  const handleOnChange = (newValue: ChangeEvent<HTMLInputElement>) => {
    onChange([
      isFirstValueComponent ? parseFloat(newValue.target.value) : firstValue,
      isSecondValueComponent ? parseFloat(newValue.target.value) : secondValue,
    ]);
  };

  const handleOnBlur = () => {
    const modifyFirstValue = Number.isNaN(firstValue)
      ? 0
      : Math.max(props.min ?? firstValue, Math.min(firstValue, secondValue));
    const newFirstValue = isFirstValueComponent ? modifyFirstValue : firstValue;

    const modifySecondValue = Number.isNaN(secondValue)
      ? props.max ?? firstValue
      : Math.max(firstValue, Math.min(props.max ?? secondValue, secondValue));
    const newSecondValue = isSecondValueComponent
      ? modifySecondValue
      : secondValue;

    onChange([newFirstValue, newSecondValue]);
  };

  return (
    <TextInputField
      {...props}
      type="number"
      variant={variant}
      placeholder={props.placeholder?.[indexToModify] ?? ''}
      value={Number.isNaN(value[indexToModify]) ? '' : value[indexToModify]}
      onChange={(newValue) => handleOnChange(newValue)}
      onBlur={() => handleOnBlur()}
    />
  );
};

export default SliderInput;

const TextInputWrap: FC<PropsWithChildren<{ shouldWrap?: boolean }>> = ({
  children,
  shouldWrap,
}) => {
  if (shouldWrap) return <div className="flex flex-row gap-8">{children}</div>;

  return <>{children}</>;
};
